import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../services/api';
import { formatGradeSectionName, truncateTaskTitle } from '../../utils/formatters';
import AudioMissingConfirm from '../common/AudioMissingConfirm';

const buildSuggestedTitle = (video) => {
  const topic = video?.topic || video?.title || '';
  return truncateTaskTitle(topic || `${video?.subject || ''} Video`, 40, { fallback: 'Video', suffix: '…' });
};

const DEFAULT_TRIGGER_CLASSNAME = 'flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors';

const VideoAssignmentView = ({ video, onAssigned, onBack, target, triggerClassName }) => {
  const suggestedTitle = useMemo(() => buildSuggestedTitle(video), [video]);
  const [showModal, setShowModal] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    title: suggestedTitle,
    description: '',
    target_type: 'child',
    target_ids: [],
  });
  const [assignmentTargets, setAssignmentTargets] = useState({ students: [], batches: [], children: [] });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  // Pre-assign audio warning. `audioPrompt` is { missing, total } when
  // narration audio hasn't been generated for every narrated slide;
  // null otherwise. The actual assignment is gated behind the user's
  // decision so silent decks never go out by accident.
  const [audioPrompt, setAudioPrompt] = useState(null);
  const [audioGenerating, setAudioGenerating] = useState(false);

  const filteredBatches = useMemo(() => {
    const normalizedSubject = (video?.subject || '').toLowerCase();
    if (!normalizedSubject) return assignmentTargets.batches || [];
    return (assignmentTargets.batches || []).filter(
      (batch) => (batch.subject || '').toLowerCase() === normalizedSubject
    );
  }, [assignmentTargets.batches, video?.subject]);

  const targetBatchId = useMemo(() => {
    const rawId = target?.id;
    if (!rawId) return null;
    if (typeof rawId === 'string' && rawId.startsWith('batch_')) {
      const parsed = Number.parseInt(rawId.replace('batch_', ''), 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
    const matched = (assignmentTargets.batches || []).find((b) => String(b.id) === String(rawId));
    return matched?.id ?? null;
  }, [assignmentTargets.batches, target?.id]);

  const filteredStudents = useMemo(() => {
    const batchIds = selectedBatches.length > 0
      ? selectedBatches
      : targetBatchId
        ? [targetBatchId]
        : [];
    if (batchIds.length === 0) return [];
    const normalizedIds = new Set(batchIds.map(String));
    return (assignmentTargets.students || []).filter((student) => {
      const studentBatchIds = Array.isArray(student.batch_ids)
        ? student.batch_ids.map(String)
        : student.batch_id != null
          ? [String(student.batch_id)]
          : [];
      return studentBatchIds.some((bid) => normalizedIds.has(bid));
    });
  }, [assignmentTargets.students, selectedBatches, targetBatchId]);

  useEffect(() => {
    loadTargets();
  }, []);

  // Pre-select default recipients based on context
  useEffect(() => {
    if (!userRole) return;
    const hasSelected = selectedStudents.length > 0 || selectedBatches.length > 0;

    if (['coaching_teacher', 'school_teacher'].includes(userRole)) {
      if (targetBatchId && filteredBatches.some((b) => String(b.id) === String(targetBatchId))) {
        if (!selectedBatches.some((id) => String(id) === String(targetBatchId))) {
          setAssignmentData((prev) => ({ ...prev, target_type: 'batch' }));
          setSelectedBatches([targetBatchId]);
        }
        return;
      }
      if (!hasSelected && filteredBatches.length > 0) {
        setAssignmentData((prev) => ({ ...prev, target_type: 'batch' }));
        setSelectedBatches([filteredBatches[0].id]);
      }
    } else {
      if (!hasSelected) {
        if (target?.id) {
          setSelectedStudents([target.id]);
        } else if (assignmentTargets.children?.length > 0) {
          setSelectedStudents([assignmentTargets.children[0].id]);
        }
      }
    }
  }, [userRole, target?.id, targetBatchId, assignmentTargets, filteredBatches]);

  const loadTargets = async () => {
    try {
      const { data } = await axios.get('/auth/me');
      const role = data.role;
      setUserRole(role);

      if (['coaching_teacher', 'school_teacher'].includes(role)) {
        setAssignmentData((prev) => ({ ...prev, target_type: 'batch' }));
        const res = await axios.get('/coaching/assignment-targets');
        setAssignmentTargets(res.data);
      } else {
        const res = await axios.get('/children');
        setAssignmentTargets({
          students: [],
          batches: [],
          children: res.data.children || [],
        });
      }
    } catch (err) {
      console.error('VideoAssignmentView: error loading targets', err);
    }
  };

  // Quick local check on the video prop — TTS audio is stored as
  // base64 on each slide, so we can flag missing narration without an
  // extra round-trip when the video object is loaded with slides.
  const audioStatus = useMemo(() => {
    const slides = video?.slides || [];
    const withNarration = slides.filter((s) => (s?.narration || '').trim());
    if (withNarration.length === 0) return { total: 0, missing: 0 };
    const missing = withNarration.filter((s) => !s?.audio_b64).length;
    return { total: withNarration.length, missing };
  }, [video?.slides]);

  const handleAssign = async () => {
    if (loading) return;
    const isTeacher = ['coaching_teacher', 'school_teacher'].includes(userRole);

    if (isTeacher) {
      if (assignmentData.target_type === 'child' && selectedStudents.length === 0) {
        alert('Please select at least one student');
        return;
      }
      if (assignmentData.target_type === 'batch' && selectedBatches.length === 0) {
        alert('Please select at least one batch');
        return;
      }
    } else {
      if (selectedStudents.length === 0) {
        alert('Please select at least one child');
        return;
      }
    }

    // Surface the warning before sending students a silent deck. The
    // user picks Generate / Skip / Cancel and we either gate or
    // proceed accordingly.
    if (audioStatus.total > 0 && audioStatus.missing > 0) {
      setAudioPrompt({ missing: audioStatus.missing, total: audioStatus.total });
      return;
    }

    await performAssign();
  };

  const performAssign = async () => {
    setLoading(true);
    try {
      const payload = {
        ...assignmentData,
        target_ids: assignmentData.target_type === 'child' ? selectedStudents : selectedBatches,
      };
      await axios.post(`/videos/${video.video_id}/assign`, payload);
      setShowModal(false);
      if (onAssigned) onAssigned();
      alert('Video assigned successfully!');
    } catch (err) {
      console.error('VideoAssignmentView: assign error', err);
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to assign video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAudioGenerate = async () => {
    if (audioGenerating) return;
    setAudioGenerating(true);
    try {
      await axios.post(`/videos/${video.video_id}/audio`, { voice: video?.voice || 'nova' });
      setAudioPrompt(null);
      await performAssign();
    } catch (err) {
      console.error('VideoAssignmentView: audio generation failed', err);
      // Keep the prompt up so the user can decide: retry generate,
      // assign without audio, or cancel altogether.
      const detail = err?.response?.data?.detail;
      alert(typeof detail === 'string'
        ? `Audio generation failed: ${detail}`
        : 'Audio generation failed. You can try again, assign without audio, or cancel.');
    } finally {
      setAudioGenerating(false);
    }
  };

  const handleAudioSkip = () => {
    setAudioPrompt(null);
    performAssign();
  };

  const handleAudioCancel = () => {
    if (audioGenerating) return;
    setAudioPrompt(null);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={triggerClassName || DEFAULT_TRIGGER_CLASSNAME}
      >
        👥 Assign
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs uppercase text-purple-100 tracking-wide">Assign Video</p>
                  <h4 className="text-xl font-semibold text-white">Send Video to Students</h4>
                  <p className="text-sm text-purple-100">We preselect recipients from your current context.</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                  className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              {/* Video info */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-xl">🎬</div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{video.title}</p>
                    <p className="text-xs text-gray-500">{video.subject} • {video.total_slides} slides</p>
                  </div>
                </div>
              </div>

              {/* Title & Description */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Title *</label>
                  <input
                    type="text"
                    value={assignmentData.title}
                    onChange={(e) => setAssignmentData({ ...assignmentData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 bg-gray-50"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Description (Optional)</label>
                  <textarea
                    value={assignmentData.description}
                    onChange={(e) => setAssignmentData({ ...assignmentData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 bg-gray-50"
                    rows="2"
                    placeholder="Add instructions for students..."
                  />
                </div>
              </div>

              {/* Teacher: Batch vs Individual */}
              {['coaching_teacher', 'school_teacher'].includes(userRole) && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
                  <label className="block text-sm font-semibold text-gray-800">Assign To</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['child', 'batch'].map((type) => (
                      <label
                        key={type}
                        className={`flex items-center space-x-2 p-3 rounded-lg border ${
                          assignmentData.target_type === type
                            ? 'border-indigo-200 bg-indigo-50 shadow-inner'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="target_type"
                          value={type}
                          checked={assignmentData.target_type === type}
                          onChange={(e) => setAssignmentData({ ...assignmentData, target_type: e.target.value })}
                          className="text-indigo-600"
                        />
                        <span className="text-sm text-gray-800">
                          {type === 'child' ? 'Individual Students' : 'Batches'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Teacher: Student selector */}
              {['coaching_teacher', 'school_teacher'].includes(userRole) && assignmentData.target_type === 'child' && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Students</label>
                    <span className="text-xs text-gray-500">Multi-select allowed</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {filteredStudents.length === 0 ? (
                      <p className="text-sm text-gray-500 px-2 py-1">No students in this batch.</p>
                    ) : (
                      filteredStudents.map((student) => (
                        <label key={student.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={(e) => {
                              setSelectedStudents(
                                e.target.checked
                                  ? [...selectedStudents, student.id]
                                  : selectedStudents.filter((id) => id !== student.id)
                              );
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-800">{student.name} (Grade {student.grade})</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Teacher: Batch selector */}
              {['coaching_teacher', 'school_teacher'].includes(userRole) && assignmentData.target_type === 'batch' && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Batches</label>
                    <span className="text-xs text-gray-500">Class-wide sharing</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50 space-y-1">
                    {filteredBatches.length === 0 ? (
                      <p className="text-sm text-gray-500 px-2 py-1">
                        No batches available{video?.subject ? ` for ${video.subject}` : ''}. Update the subject or create a matching batch.
                      </p>
                    ) : (
                      filteredBatches.map((batch) => (
                        <label key={batch.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                          <input
                            type="checkbox"
                            checked={selectedBatches.includes(batch.id)}
                            onChange={(e) => {
                              setSelectedBatches(
                                e.target.checked
                                  ? [...selectedBatches, batch.id]
                                  : selectedBatches.filter((id) => id !== batch.id)
                              );
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-800">
                            {userRole === 'school_teacher'
                              ? `${formatGradeSectionName(batch.name, batch.grade)} - ${batch.subject}`
                              : `${batch.name} - ${batch.subject} (Grade ${batch.grade})`}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Parent: Children selector */}
              {!['coaching_teacher', 'school_teacher'].includes(userRole) && (
                <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-800">Select Children</label>
                    <span className="text-xs text-gray-500">Tap to include/exclude</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                    {(assignmentTargets.children || []).map((child) => (
                      <label key={child.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(child.id)}
                          onChange={(e) => {
                            setSelectedStudents(
                              e.target.checked
                                ? [...selectedStudents, child.id]
                                : selectedStudents.filter((id) => id !== child.id)
                            );
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-800">{child.name} (Grade {child.grade})</span>
                      </label>
                    ))}
                  </div>
                  {selectedStudents.length > 0 && (
                    <p className="text-xs text-green-600 mt-2">{selectedStudents.length} child(ren) selected</p>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center space-x-2 pt-2">
                <p className="text-xs text-gray-500">Students will receive the video instantly in their workspace.</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowModal(false)}
                    className="py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={loading}
                    className="py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 shadow"
                  >
                    {loading ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {audioPrompt && (
        <AudioMissingConfirm
          missingCount={audioPrompt.missing}
          totalCount={audioPrompt.total}
          generating={audioGenerating}
          onGenerate={handleAudioGenerate}
          onSkip={handleAudioSkip}
          onCancel={handleAudioCancel}
        />
      )}
    </>
  );
};

export default VideoAssignmentView;
